import { WhatsNewActions, useGetLatestNewsFunctionRequest, usePostNewsUserActionRequest } from "shared/request/myHealthyAdvantageApi";
import { useEffect, useState } from "react";

import { Carousel } from "UIPalette/Carousel/Carousel";
import { ReactComponent as CrossIcon } from "shared/assets/Icons/cross.svg";
import { EventNames } from "core/monitoring/types/enums";
import { Modal } from "shared/UI/Modal/Modal";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { ReactComponent as ThumbIcon } from "shared/assets/Icons/thumb.svg";
import { WhatsNewItem } from "./WhatsNewItem/WhatsNewItem";
import { WhatsNewState } from "core/state/whatsNewState";
import { appInsightsLogger } from "core/monitoring/AppInsights";
import classNames from "classnames";
import { trackEvents } from "core/monitoring/Events";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { useModalContext } from "core/state/ModalContext/ModalContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const WhatsNewModal = () => {
  const miniDisplay = !useMatchMedia(ResponsiveBreakpoints.Mini, false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const navigate = useNavigate();

  const { t } = useTranslation(undefined, { keyPrefix: "whats-new" });

  const { show, hide, isVisible } = useModalContext("whats-new");

  const { data: latestNews } = useGetLatestNewsFunctionRequest();

  const { trigger } = usePostNewsUserActionRequest();

  const triggerResponse = (action: WhatsNewActions) =>
    trigger({
      body: {
        newsId: latestNews!.id,
        action,
      },
    });

  const handleFeedback = (action: WhatsNewActions, event: EventNames) => {
    trackEvents(event);
    try {
      triggerResponse(action);
    } catch (e) {
      appInsightsLogger.error(`Failed to send news user action (${action})`, e);
    }
    hide();
    const targetUrl = latestNews?.items[currentSlide]?.url;
    if (targetUrl && action === WhatsNewActions.INTERESTED) {
      navigate(targetUrl);
    }
  };

  const handleDismissed = () => handleFeedback(WhatsNewActions.DISMISSED, EventNames.WHATSNEW_DISMISSED);
  const handleInterested = () => handleFeedback(WhatsNewActions.INTERESTED, EventNames.WHATSNEW_INTERESTED);
  const handleNotInterested = () => handleFeedback(WhatsNewActions.NOT_INTERESTED, EventNames.WHATSNEW_NOT_INTERESTED);
  const handleRemindLater = () => handleFeedback(WhatsNewActions.REMIND_LATER, EventNames.WHATSNEW_REMIND_LATER);

  const handleCarouselUpdate = (index: number) => setCurrentSlide(index);

  const getBtnClasses = (colorClass: string) =>
    classNames("flex items-center justify-center rounded-full shrink-0", miniDisplay ? "w-10 h-10" : "w-12 h-12", colorClass);

  useEffect(() => {
    if (latestNews && !WhatsNewState.get()) {
      show();
      WhatsNewState.set(true);
    }
  }, [latestNews, show]);

  if (!latestNews || !isVisible) {
    return null;
  }

  return (
    <Modal width="xs">
      <div data-testid="whats-new-modal" className="relative">
        <button data-testid="whats-new-dismissed" onClick={handleDismissed} className="absolute z-10 w-4 h-4 top-4 right-4">
          <CrossIcon className="fill-brand-grey hover:fill-brand-grey-light" />
        </button>
        <div className={classNames("relative flex flex-col gap-4", miniDisplay ? "text-sm" : "text-md")}>
          <div className={"whats-new-carousel"}>
            {latestNews.items.length === 1 ? (
              <WhatsNewItem item={latestNews.items[0]} />
            ) : (
              <div data-testid="whats-new-carousel">
                <Carousel onUpdated={handleCarouselUpdate} itemsPerSlide={1} ariaLabel={t("aria")}>
                  {latestNews!.items.map((item: any, index: number) => (
                    <WhatsNewItem key={`${latestNews?.id}_slide_${index}`} item={item} />
                  ))}
                </Carousel>
              </div>
            )}
          </div>
          <hr className="my-2 mx-7 text-brand-grey"></hr>
          <div className="flex flex-col gap-5 px-7 pb-7">
            {latestNews.showReporting && (
              <div data-testid="whats-new-feedback-buttons" className="flex items-center justify-between gap-2 ">
                <h4>{t("feedbackText")}</h4>
                <div className="flex items-center gap-4">
                  <button
                    data-testid="whats-new-interested"
                    onClick={handleInterested}
                    className={getBtnClasses("bg-brand-green hover:bg-brand-green-light")}
                  >
                    <ThumbIcon />
                  </button>
                  <button
                    data-testid="whats-new-not-interested"
                    onClick={handleNotInterested}
                    className={getBtnClasses("bg-brand-grey hover:bg-brand-grey-light")}
                  >
                    <ThumbIcon className="rotate-180" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <button data-testid="whats-new-remind-later" onClick={handleRemindLater} className="text-navy-blue hover:text-navy-blue-light">
                {t("remindMeLater")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
